import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Loader2, 
  ClipboardList, 
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  PlayCircle,
  Filter,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { getTasks } from '@/api/project';
import type { Task } from '@/types/project';

function getStatusBadge(status: string) {
  const lowerStatus = status.toLowerCase();
  
  if (lowerStatus === 'completed') {
    return <Badge className="bg-green-600 text-white min-w-[90px] justify-center"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
  }
  if (lowerStatus === 'running') {
    return <Badge className="bg-blue-600 text-white min-w-[90px] justify-center"><PlayCircle className="h-3 w-3 mr-1 animate-pulse" />Running</Badge>;
  }
  if (lowerStatus === 'failed') {
    return <Badge className="bg-red-600 text-white min-w-[90px] justify-center"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
  }
  if (lowerStatus === 'pending') {
    return <Badge className="bg-gray-200 text-gray-700 min-w-[90px] justify-center"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  }
  if (lowerStatus === 'cancelled') {
    return <Badge className="bg-yellow-500 text-gray-800 min-w-[90px] justify-center"><X className="h-3 w-3 mr-1" />Cancelled</Badge>;
  }
  return <Badge variant="secondary" className="min-w-[90px] justify-center">{status}</Badge>;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

function TaskRow({ task, projectId, onClick }: { task: Task; projectId: string; onClick: () => void }) {
  // Calculate duration for running tasks
  const displayDuration = task.status === 'running' && task.started_at
    ? (Date.now() - new Date(task.started_at).getTime()) / 1000
    : task.duration;

  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
    >
      <td className="py-3 px-4 border-b">
        <div>
          <p className="font-medium text-primary hover:underline">
            {task.title || 'Untitled Task'}
          </p>
          {task.description && (
            <p className="text-sm text-gray-500 line-clamp-1">{task.description}</p>
          )}
        </div>
      </td>
      <td className="py-3 px-4 border-b text-center">
        {getStatusBadge(task.status)}
      </td>
      <td className="py-3 px-4 border-b text-sm">
        {task.created_at ? new Date(task.created_at).toLocaleString() : '-'}
      </td>
      <td className="py-3 px-4 border-b text-center text-sm">
        {formatDuration(displayDuration)}
      </td>
      <td className="py-3 px-4 border-b">
        {task.build_name ? (
          <div>
            <span 
              className="text-primary hover:underline text-sm"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `/project/${projectId}/deployments`;
              }}
            >
              {task.build_name}
            </span>
            {task.container_name && (
              <p className="text-xs text-gray-400">{task.container_name}</p>
            )}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="py-3 px-4 border-b text-sm text-gray-500">
        {task.created_by || '-'}
      </td>
    </tr>
  );
}

export function TasksPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['tasks', projectId, page, statusFilter, typeFilter],
    queryFn: () => getTasks(projectId!, page, { 
      status: statusFilter || undefined, 
      type: typeFilter || undefined 
    }),
    enabled: !!projectId,
    refetchInterval: autoRefresh ? 30000 : false, // 30 seconds when enabled
  });

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);
  
  // Check if currently refreshing (for button feedback)
  const isRefreshing = isFetching && !isLoading;

  // Clear filters
  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setSearchQuery('');
    setPage(1);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load tasks. You may not have permission to view this page (admin only).
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const tasks = data?.tasks || [];
  const pagination = data?.pagination;
  const taskTypes = data?.task_types || [];
  const taskStatuses = data?.task_statuses || [];

  // Filter by search locally
  const filteredTasks = searchQuery 
    ? tasks.filter(t => 
        t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tasks;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6" />
              Task History
            </h1>
            <p className="text-gray-500 mt-1 flex items-center gap-2">
              View and manage all task operations
              <Badge 
                className={cn(
                  "text-xs",
                  autoRefresh ? "bg-green-600 text-white" : "bg-gray-200 text-gray-600"
                )}
              >
                Auto Refresh: {autoRefresh ? 'ON' : 'OFF'}
              </Badge>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant={autoRefresh ? "destructive" : "outline"}
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", autoRefresh && "animate-spin")} />
              {autoRefresh ? 'Disable' : 'Enable'} Auto Refresh
            </Button>
            <Button onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
            </Button>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Task Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {taskStatuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Select value={typeFilter || 'all'} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {taskTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search tasks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {(statusFilter || typeFilter || searchQuery) && (
                    <Button variant="outline" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Table */}
        <Card>
          <CardHeader>
            <CardTitle>Tasks</CardTitle>
            <CardDescription>
              {pagination?.total || 0} total tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No tasks found</p>
                <p className="text-sm mb-4">No tasks match your current filter criteria.</p>
                {(statusFilter || typeFilter || searchQuery) && (
                  <Button variant="outline" onClick={clearFilters}>
                    <Filter className="h-4 w-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Task</th>
                        <th className="py-3 px-4 text-center text-sm font-medium text-gray-500 border-b" style={{ width: '110px' }}>Status</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b" style={{ width: '180px' }}>Created</th>
                        <th className="py-3 px-4 text-center text-sm font-medium text-gray-500 border-b" style={{ width: '100px' }}>Duration</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Deployment</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b" style={{ width: '150px' }}>Initiated By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTasks.map((task) => (
                        <TaskRow 
                          key={task.id} 
                          task={task} 
                          projectId={projectId!} 
                          onClick={() => navigate(`/project/${projectId}/tasks/${task.id}`)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-sm text-gray-500">
                      Page {pagination.page} of {pagination.pages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={!pagination.has_prev}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={!pagination.has_next}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
